import ExcelJS from 'exceljs';
import moment from 'moment';

/**
 * Common function to download data as Excel using ExcelJS
 * @param {Object} config - Configuration object
 * @param {Array} config.data - Array of data objects to export
 * @param {string} config.fileName - Name of the file to download (without extension)
 * @param {Array} config.columns - Array of column definitions with { header: string, key: string, width?: number }
 * @param {string} config.sheetName - Name of the worksheet (default: 'Sheet1')
 * @param {Function} config.dataFormatter - Optional function to format each row before adding to Excel
 * @param {boolean} config.reverseData - Whether to reverse the data array (default: false)
 * @param {Object} config.headerStyle - Optional custom header style
 * @param {Function} config.cellStyler - Optional function to style cells after rows are added: (worksheet, rowNumber, columnKey, cell, rowData) => void
 * @param {Function} config.setDownloadLoading - Optional function to set loading state
 * @returns {Promise<void>}
 */
export const downloadDataAsExcel = async ({
  data,
  fileName,
  columns,
  sheetName = 'Sheet1',
  dataFormatter = null,
  reverseData = false,
  headerStyle = null,
  cellStyler = null,
  setDownloadLoading = null,
}) => {
  if (setDownloadLoading) {
    setDownloadLoading(true);
  }

  try {
    const workbook = new ExcelJS.Workbook();
    let dataList = data || [];

    if (dataList.length === 0) {
      if (setDownloadLoading) {
        setDownloadLoading(false);
      }
      return;
    }

    // Reverse data if needed
    if (reverseData) {
      dataList = [...dataList].reverse();
    }

    // Create worksheet
    const worksheet = workbook.addWorksheet(sheetName);
    const headerRow = worksheet.getRow(1);
    headerRow.height = 20;

    // Set columns
    worksheet.columns = columns.map((col) => ({
      header: col.header,
      key: col.key,
      width: col.width || 20,
    }));

    // Style the header row
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      if (headerStyle) {
        Object.assign(cell.font, headerStyle.font || {});
        Object.assign(cell.alignment, headerStyle.alignment || {});
        if (headerStyle.fill) {
          cell.fill = headerStyle.fill;
        }
      }
    });

    // Format and add data rows
    const formattedRows = dataFormatter
      ? dataList.map((record) => dataFormatter(record))
      : dataList;

    worksheet.addRows(formattedRows);

    // Apply custom cell styling if provided
    if (cellStyler) {
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header
        const rowData = formattedRows[rowNumber - 2]; // rowNumber is 1-indexed, formattedRows is 0-indexed
        row.eachCell((cell, colNumber) => {
          const column = columns[colNumber - 1];
          if (column) {
            cellStyler(worksheet, rowNumber, column.key, cell, rowData);
          }
        });
      });
    }

    // Convert to buffer and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const finalFileName = fileName.includes('.xlsx') ? fileName : `${fileName} ${moment().format('YYYY-MM-DD')}.xlsx`;
    link.download = finalFileName;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading Excel:', error);
  } finally {
    if (setDownloadLoading) {
      setDownloadLoading(false);
    }
  }
};

